/**
 * Utility functions for handling file downloads and file viewing
 * Reusable across the application for downloads, attachments, and file management
 */

export const looksLikeUrl = (value) =>
  typeof value === 'string' &&
  (value.startsWith('http://') || value.startsWith('https://') || value.includes('://'));

/**
 * Resolve the HTTP(S) URL to fetch for download from a file object or string.
 * Service/product attachments may store the URL only on `name` (DB) or only on `url` (uploads).
 */
export const getAttachmentDownloadUrl = (file) => {
  if (!file) return '';
  if (typeof file === 'string') return looksLikeUrl(file) ? file : '';
  const candidates = [
    file.url,
    file.file_url,
    file._fileUrl,
    file._downloadUrl,
    file.preview,
    file.name,
  ];
  return candidates.find((c) => c && looksLikeUrl(c)) || '';
};

/**
 * Short display/download filename (not a URL).
 */
export const getAttachmentDisplayName = (file) => {
  if (!file || typeof file === 'string') return null;
  if (file.displayName && !looksLikeUrl(file.displayName)) return file.displayName;
  if (file.name && !looksLikeUrl(file.name)) return file.name;
  return null;
};

const triggerBrowserDownload = (blob, displayFileName) => {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = displayFileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  requestAnimationFrame(() => {
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }, 250);
  });
};

const tryDirectUrlDownload = (fileUrl, displayFileName) => {
  const link = document.createElement('a');
  link.href = fileUrl;
  link.download = displayFileName;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  requestAnimationFrame(() => {
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
    }, 250);
  });
};

const isValidDownloadBlob = (blob, response) => {
  if (!blob || blob.size === 0) return false;
  const type = (blob.type || response?.headers?.get('content-type') || '').toLowerCase();
  if (type.includes('text/html') || type.includes('application/json')) return false;
  return true;
};

/**
 * Resolve a short, filesystem-safe download name from file metadata or URL.
 * DB/API often stores the full public URL in `name` — avoid using that as the download filename.
 * @param {string} fileUrl - File URL used for download
 * @param {string|null} preferredName - Optional display name from file object
 * @returns {string}
 */
export const resolveDownloadFileName = (fileUrl, preferredName = null) => {
  if (preferredName && !looksLikeUrl(preferredName)) {
    return preferredName.split('/').pop()?.split('?')[0] || preferredName;
  }

  const targetUrl = resolveAttachmentProxyPath(fileUrl);
  const fromUrl = targetUrl?.split('/').pop()?.split('?')[0] || 'download';
  try {
    return decodeURIComponent(fromUrl.replace(/^\d+_/, ''));
  } catch {
    return fromUrl.replace(/^\d+_/, '');
  }
};

/**
 * Download a file from URL as a blob
 * @param {string} fileUrl - URL of the file to download
 * @param {string} fileName - Name to save the file as, or provide in URL
 * @param {function} onSuccess - Callback function on successful download
 * @param {function} onError - Callback function on error
 * @returns {Promise<void>}
 */
export const downloadFileFromUrl = async (
  fileUrl,
  fileName = null,
  onSuccess = null,
  onError = null
) => {
  const downloadUrl = looksLikeUrl(fileUrl) ? fileUrl : '';
  const displayFileName = resolveDownloadFileName(downloadUrl || fileUrl, fileName);

  try {
    if (!downloadUrl) {
      throw new Error('No valid file URL provided');
    }

    const response = await fetch(downloadUrl, { mode: 'cors' });
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    const blob = await response.blob();

    if (!isValidDownloadBlob(blob, response)) {
      tryDirectUrlDownload(downloadUrl, displayFileName);
      if (onSuccess) onSuccess();
      return;
    }

    triggerBrowserDownload(blob, displayFileName);
    if (onSuccess) onSuccess();
  } catch (error) {
    console.error('Download error:', error);
    try {
      if (downloadUrl) {
        tryDirectUrlDownload(downloadUrl, displayFileName);
        if (onSuccess) onSuccess();
        return;
      }
    } catch (fallbackError) {
      console.error('Direct download fallback failed:', fallbackError);
    }
    if (onError) onError(error);
    throw error;
  }
};

/**
 * Download an image with custom naming
 * @param {string} imageUrl - URL of the image to download
 * @param {string} imageName - Custom name for the image (optional)
 * @param {number} imageIndex - Index for naming purposes
 * @param {function} onSuccess - Callback function on successful download
 * @param {function} onError - Callback function on error
 * @returns {Promise<void>}
 */
export const downloadImage = async (
  imageUrl,
  imageName = null,
  imageIndex = 0,
  onSuccess = null,
  onError = null
) => {
  const downloadUrl = looksLikeUrl(imageUrl) ? imageUrl : '';
  const ext = getFileExtension(downloadUrl || imageUrl) || 'jpg';
  const resolvedName = resolveDownloadFileName(downloadUrl || imageUrl, imageName);
  const fileName =
    resolvedName !== 'download'
      ? resolvedName
      : `image-${imageIndex + 1}-${Date.now()}.${ext}`;

  try {
    if (!downloadUrl) {
      throw new Error('No valid image URL provided');
    }

    const response = await fetch(downloadUrl, { mode: 'cors' });
    if (!response.ok) {
      throw new Error('Image download failed');
    }
    const blob = await response.blob();

    if (!isValidDownloadBlob(blob, response)) {
      tryDirectUrlDownload(downloadUrl, fileName);
      if (onSuccess) onSuccess();
      return;
    }

    triggerBrowserDownload(blob, fileName);
    if (onSuccess) onSuccess();
  } catch (error) {
    console.error('Image download error:', error);
    try {
      if (downloadUrl) {
        tryDirectUrlDownload(downloadUrl, fileName);
        if (onSuccess) onSuccess();
        return;
      }
    } catch (fallbackError) {
      console.error('Image direct download fallback failed:', fallbackError);
    }
    if (onError) onError(error);
    throw error;
  }
};

/**
 * Open a file in a new window with appropriate viewer
 * @param {string} fileUrl - URL of the file
 * @param {object} options - Configuration options
 * @param {boolean} options.isActualPdf - Whether file is PDF
 * @param {boolean} options.isOfficeFile - Whether file is Office format
 * @param {boolean} options.isClient - Whether user is a client
 * @param {boolean} options.hideToolbar - Hide PDF toolbar
 * @param {string} options.fileName - File name for title
 * @returns {void}
 */
/**
 * Build the HTML document written into a blank popup window to preview a file.
 * Pulled out of openFileInViewer so other code (e.g. the global window.open/document.write
 * patch) can (re)build correct viewer markup for a URL without opening a new window itself.
 * @param {string} fileUrl - URL of the file
 * @param {object} options - Configuration options
 * @param {boolean} options.isActualPdf - Whether file is PDF
 * @param {boolean} options.isOfficeFile - Whether file is Office format
 * @param {boolean} options.hideToolbar - Hide PDF toolbar
 * @param {string} options.fileName - File name for title
 * @param {string} options.fileType - MIME type, used for image detection
 * @returns {string}
 */
export const buildFileViewerHtml = (fileUrl, options = {}) => {
  const {
    isActualPdf = false,
    isOfficeFile = false,
    hideToolbar = true,
    fileName = 'File',
    fileType = '',
  } = options;

  const ext = getFileExtension(fileUrl);
  const isImage =
    isImageFile(fileType) ||
    ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext);
  // Our own backend proxy URL — the browser fetches it directly, so it always works even
  // on private/dev hosts. External viewers (Google/Office) fetch server-side and fail with
  // a blank "No preview available" when they can't reach a non-public environment.
  const isOwnAttachmentUrl =
    typeof fileUrl === 'string' && fileUrl.includes('/webservice/attachments/file');

  if (isActualPdf) {
    const toolbarParams = hideToolbar ? '#toolbar=0&navpanes=0&scrollbar=0' : '';
    return `
        <html>
          <head>
            <title>PDF Viewer</title>
            <style>
              body { margin: 0; padding: 0; overflow: hidden; }
              iframe { width: 100vw; height: 100vh; border: none; }
            </style>
          </head>
          <body>
            <iframe src="${fileUrl}${toolbarParams}" title="PDF Viewer"></iframe>
          </body>
        </html>
      `;
  }

  if (isOfficeFile) {
    const officeViewerSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
      fileUrl
    )}`;
    return `
        <html>
          <head>
            <title>${fileName}</title>
            <style>
              body { margin: 0; padding: 0; overflow: hidden; }
              iframe { width: 100vw; height: 100vh; border: none; }
            </style>
          </head>
          <body>
            <iframe src="${officeViewerSrc}" title="${fileName}"></iframe>
          </body>
        </html>
      `;
  }

  if (isImage) {
    // Direct image preview (Google Docs Viewer cannot render images)
    return `
        <html>
          <head>
            <title>${fileName}</title>
            <style>
              body { margin: 0; padding: 0; min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #111; }
              img { max-width: 100%; max-height: 100vh; object-fit: contain; }
            </style>
          </head>
          <body>
            <img src="${fileUrl}" alt="${fileName}" />
          </body>
        </html>
      `;
  }

  if (isOwnAttachmentUrl) {
    // Unknown type but our own URL — render it directly rather than routing through an
    // external viewer that may not be able to reach this environment.
    return `
        <html>
          <head>
            <title>${fileName}</title>
            <style>
              body { margin: 0; padding: 0; overflow: hidden; }
              iframe { width: 100vw; height: 100vh; border: none; }
            </style>
          </head>
          <body>
            <iframe src="${fileUrl}" title="${fileName}"></iframe>
          </body>
        </html>
      `;
  }

  // Genuinely external URL of unknown type — best effort via Google Docs Viewer.
  const googleViewerSrc = `https://docs.google.com/gview?url=${encodeURIComponent(
    fileUrl
  )}&embedded=true`;
  return `
        <html>
          <head>
            <title>${fileName}</title>
            <style>
              body { margin: 0; padding: 0; overflow: hidden; }
              iframe { width: 100vw; height: 100vh; border: none; }
            </style>
          </head>
          <body>
            <iframe src="${googleViewerSrc}" title="${fileName}" sandbox="allow-scripts allow-same-origin"></iframe>
          </body>
        </html>
      `;
};

export const openFileInViewer = (
  fileUrl,
  options = {}
) => {
  try {
    if (!fileUrl) {
      throw new Error('No file URL provided');
    }

    const newWindow = window.open('', '_blank');
    newWindow.document.write(buildFileViewerHtml(fileUrl, options));
    newWindow.document.close();
  } catch (error) {
    console.error('Error opening file viewer:', error);
    throw error;
  }
};

/**
 * Get embedded iframe source for file preview
 * For clients or restricted viewing
 * @param {string} fileUrl - URL of the file
 * @param {object} options - Configuration options
 * @param {boolean} options.isActualPdf - Whether file is PDF
 * @param {string} options.hideToolbar - Hide PDF toolbar parameters
 * @returns {string} - iframe src URL
 */
export const getEmbeddedFileSource = (
  fileUrl,
  options = {}
) => {
  const {
    isActualPdf = false,
    hideToolbar = true,
  } = options;

  if (!fileUrl) return '';

  if (isActualPdf) {
    const toolbarParams = hideToolbar
      ? '&toolbar=0&navpanes=0&scrollbar=0'
      : '';
    return `https://docs.google.com/gview?url=${encodeURIComponent(
      fileUrl
    )}&embedded=true${toolbarParams}`;
  }

  // Default to Google Docs Viewer for other formats
  return `https://docs.google.com/gview?url=${encodeURIComponent(
    fileUrl
  )}&embedded=true`;
};

/**
 * Backend attachment proxy URLs encode the real nested path/filename in a `path` query
 * param (e.g. `.../attachments/file?path=folder%2Fsub%2Fname.pdf`) — the URL's own last
 * path segment is just "file", so resolve the real decoded name from `path` when present.
 * @param {string} fileUrl
 * @returns {string}
 */
export const resolveAttachmentProxyPath = (fileUrl) => {
  if (typeof fileUrl !== 'string' || !fileUrl.includes('/attachments/file') || !fileUrl.includes('path=')) {
    return fileUrl;
  }
  try {
    const path = new URL(fileUrl, window.location.origin).searchParams.get('path');
    // searchParams.get already decodes once; second decode is for double-encoded DB values.
    if (path) {
      try {
        return decodeURIComponent(path);
      } catch (_) {
        return path;
      }
    }
  } catch (_) {
    /* keep original */
  }
  return fileUrl;
};

/**
 * Extract file extension from URL or fileName
 * @param {string} fileUrl - URL or file name
 * @returns {string} - File extension in lowercase
 */
export const getFileExtension = (fileUrl) => {
  if (!fileUrl) return '';
  const target = resolveAttachmentProxyPath(fileUrl);
  let ext =
    target?.split('/').pop()?.split('?')[0]?.split('.').pop()?.toLowerCase() ||
    '';
  // Proxy URLs look like `.../file?path=folder%2Fname.xlsx` — last segment is "file".
  if ((!ext || ext === 'file') && typeof fileUrl === 'string' && fileUrl.includes('path=')) {
    try {
      const path =
        new URL(fileUrl, window.location.origin).searchParams.get('path') || '';
      ext =
        path.split('/').pop()?.split('?')[0]?.split('.').pop()?.toLowerCase() ||
        '';
    } catch (_) {
      /* keep */
    }
  }
  return ext === 'file' ? '' : ext;
};

/**
 * Check if file is an Office document
 * @param {string} fileUrl - URL or file name
 * @param {string} fileType - MIME type (optional)
 * @returns {boolean} - True if Office file
 */
export const isOfficeFile = (fileUrl, fileType = '') => {
  const ext = getFileExtension(fileUrl);
  if (['xls', 'xlsx', 'doc', 'docx', 'ppt', 'pptx', 'csv'].includes(ext)) {
    return true;
  }
  const t = String(fileType || '').toLowerCase();
  return (
    t.includes('spreadsheet') ||
    t.includes('excel') ||
    t.includes('wordprocessing') ||
    t.includes('msword') ||
    t.includes('ms-excel') ||
    t.includes('ms-powerpoint') ||
    t.includes('presentationml') ||
    t.includes('officedocument') ||
    t === 'text/csv' ||
    t === 'csv'
  );
};

/**
 * Check if file is a PDF
 * @param {string} fileUrl - URL or file name
 * @param {string} fileType - MIME type (optional)
 * @returns {boolean} - True if PDF
 */
export const isPdfFile = (fileUrl, fileType = '') => {
  const ext = getFileExtension(fileUrl);
  return (
    ext === 'pdf' ||
    fileType === 'pdf' ||
    fileType === 'application/pdf'
  );
};

/**
 * Check if file is an image
 * @param {string} fileType - MIME type
 * @returns {boolean} - True if image
 */
export const isImageFile = (fileType) => {
  const nonRenderableSubtypes = [
    'eps',
    'ai',
    'ps',
    'psd',
    'x-eps',
    'x-ai',
    'postscript',
  ];
  
  if (nonRenderableSubtypes.includes(fileType?.toLowerCase())) {
    return false;
  }

  return (
    fileType?.startsWith('image/') ||
    fileType === 'img' ||
    fileType === 'image'
  );
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Download multiple attachment files one after another.
 * Uses a short delay between downloads so browsers do not block successive triggers.
 * @param {Array<object|string>} files - Attachment file objects or URL strings
 * @param {object} options
 * @param {function} options.onProgress - ({ current, total, file }) callback
 * @param {function} options.onComplete - ({ successCount, failCount, total }) callback
 * @param {number} options.delayMs - Delay between downloads (default 400)
 * @returns {Promise<{ successCount: number, failCount: number, total: number }>}
 */
export const downloadAllAttachments = async (files = [], options = {}) => {
  const { onProgress = null, onComplete = null, delayMs = 400 } = options;
  const list = (Array.isArray(files) ? files : []).filter(Boolean);
  let successCount = 0;
  let failCount = 0;

  for (let i = 0; i < list.length; i += 1) {
    const file = list[i];
    const fileUrl = getAttachmentDownloadUrl(file);
    const displayName = getAttachmentDisplayName(file);
    const fileType =
      typeof file === 'object' ? file?.file_type || file?.type || '' : '';
    const ext = getFileExtension(fileUrl);

    if (onProgress) {
      onProgress({ current: i + 1, total: list.length, file });
    }

    if (!fileUrl) {
      failCount += 1;
      continue;
    }

    try {
      if (
        isImageFile(fileType) ||
        ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)
      ) {
        await downloadImage(fileUrl, displayName, i);
      } else {
        await downloadFileFromUrl(fileUrl, displayName);
      }
      successCount += 1;
    } catch (error) {
      console.error('Download all item failed:', error);
      failCount += 1;
    }

    if (i < list.length - 1 && delayMs > 0) {
      await delay(delayMs);
    }
  }

  const result = { successCount, failCount, total: list.length };
  if (onComplete) onComplete(result);
  return result;
};

export default {
  downloadFileFromUrl,
  downloadImage,
  downloadAllAttachments,
  openFileInViewer,
  buildFileViewerHtml,
  getEmbeddedFileSource,
  getFileExtension,
  resolveDownloadFileName,
  resolveAttachmentProxyPath,
  getAttachmentDownloadUrl,
  getAttachmentDisplayName,
  looksLikeUrl,
  isOfficeFile,
  isPdfFile,
  isImageFile,
};
